// Floating status pill shown in the AppLayout while an intake is
// in-flight or recently completed. Renders nothing during the "idle"
// stage so it doesn't clutter the UI when there's no submission to
// surface.
//
// Three visual states:
//   - uploading / atlas: spinning pill in the bottom-right showing
//     the current step + project_id. Not dismissable.
//   - done: green success toast linking to /runs/:projectId/:runId.
//     Dismissable (X button).
//   - error: red error toast with the message. Dismissable.
import { Link } from "react-router-dom";
import { useIntakeSubmitContext } from "./useIntakeSubmitContext";
import styles from "./IntakeStatusPill.module.css";

export const IntakeStatusPill = () => {
  const { state, dismiss } = useIntakeSubmitContext();
  if (state.stage === "idle") return null;

  const projectLabel = state.projectId ?? "project";

  if (state.stage === "uploading" || state.stage === "atlas") {
    const pct =
      state.uploadProgress.total > 0
        ? (state.uploadProgress.current / state.uploadProgress.total) * 100
        : 0;
    return (
      <div className={`${styles.pill} ${styles.busy}`} role="status">
        <div className={styles.row}>
          <span className={styles.spinner} aria-hidden />
          <strong>Submit in progress: {projectLabel}</strong>
        </div>
        <div className={styles.label}>{state.label}</div>
        {state.stage === "uploading" && (
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        <div className={styles.hint}>
          Navigate freely — this submit keeps running. You'll be sent to the
          run page once Atlas finishes.
        </div>
      </div>
    );
  }

  if (state.stage === "done") {
    return (
      <div className={`${styles.pill} ${styles.done}`} role="status">
        <div className={styles.row}>
          <strong>✓ Atlas finished: {projectLabel}</strong>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className={styles.dismiss}
          >
            ×
          </button>
        </div>
        {state.result?.run_id ? (
          <Link
            to={`/runs/${encodeURIComponent(state.result.project_id)}/${encodeURIComponent(state.result.run_id)}`}
            className={styles.cta}
            onClick={dismiss}
          >
            Open the run →
          </Link>
        ) : (
          <Link
            to="/operations"
            className={styles.cta}
            onClick={dismiss}
          >
            Open Operations →
          </Link>
        )}
      </div>
    );
  }

  // error
  return (
    <div className={`${styles.pill} ${styles.error}`} role="alert">
      <div className={styles.row}>
        <strong>Submit failed: {projectLabel}</strong>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className={styles.dismiss}
        >
          ×
        </button>
      </div>
      <div className={styles.label}>
        {state.error?.message ?? "Unknown error"}
      </div>
    </div>
  );
};
